import { useEffect, useRef, useState } from 'react';
import { TileMap, type ArenaDef, type TilesetDef } from '@archon3d/core';
import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { dressArena } from '../scenes/arena/arenaDressing';
import { buildArenaMeshes } from '../scenes/arena/arenaMesh';
import { createArenaMaterial } from '../scenes/arena/arenaMaterial';

export function ArenaPreview({ arena, tileset }: { arena: ArenaDef; tileset: TilesetDef }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState('');
  // Built again when the arena's ground changes, not at every keystroke of its name beside the
  // preview: each build is a new WebGL engine (editor:bugs#6).
  const latest = useRef(arena);
  latest.current = arena;
  const geometry = JSON.stringify({ ...arena, name: undefined });
  useEffect(() => {
    if (!canvas.current) return;
    const arena = latest.current;
    const engine = new Engine(canvas.current, true, { preserveDrawingBuffer: true });
    const scene = new Scene(engine);
    try {
      const centre = new Vector3(arena.width / 2, 0, arena.height / 2);
      const camera = new ArcRotateCamera(
        'preview',
        Math.PI / 2,
        0.8,
        Math.max(arena.width, arena.height) * 1.6,
        centre,
        scene,
      );
      camera.attachControl(canvas.current, true);
      new HemisphericLight('sky', new Vector3(0, 1, 0), scene);
      const map = new TileMap(arena, tileset);
      const root = new TransformNode('map', scene);
      buildArenaMeshes(scene, map, createArenaMaterial(scene), root);
      // The trees, liquids, fog and lamp glows a duel draws; the scene takes them with it.
      dressArena(scene, root, arena, map);
      engine.runRenderLoop(() => scene.render());
    } catch (e) {
      setError((e as Error).message);
    }
    const observer = new ResizeObserver(() => engine.resize());
    observer.observe(canvas.current);
    return () => {
      observer.disconnect();
      scene.dispose();
      engine.dispose();
    };
  }, [geometry, tileset]);
  return (
    <section>
      <p className="hint">
        3D preview · drag to orbit, scroll to zoom. Uses the same geometry as the game.
      </p>
      {error && <p role="alert">{error}</p>}
      <canvas ref={canvas} className="arena-preview" aria-label="Arena 3D preview" />
    </section>
  );
}
